import React, { useId, useState } from "react";
import { BODY_REGIONS, bodyRegionLabel, bodyRegionSurface } from "../lib/bodyRegions";
import BodyMuscleAnatomy from "./BodyMuscleAnatomy";
import { MUSCLE_SHAPES, MUSCLE_JOINT_HITS } from "../lib/bodyMuscleContours";
import "../body-map.css";

const COPY = {
  de: {
    map: "Körperkarte", surface: "Körperseite", front: "Vorne", back: "Hinten",
    anatomy: "Anatomische Ansicht", muscle: "Muskelansicht", skeleton: "Skelettansicht",
    left: "L", right: "R", select: (label) => `${label} auswählen`,
    instruction: "Körperstelle antippen oder mit Tab und Enter auswählen.",
    hidden: "Die ausgewählte Stelle liegt auf der anderen Körperseite.",
    reveal: "Ausgewählte Stelle anzeigen",
  },
  en: {
    map: "Body map", surface: "Body surface", front: "Front", back: "Back",
    anatomy: "Anatomy view", muscle: "Muscle view", skeleton: "Skeleton view",
    left: "L", right: "R", select: (label) => `Select ${label.toLowerCase()}`,
    instruction: "Tap a body region, or use Tab and Enter to select.",
    hidden: "The selected region is on the other side of the body.",
    reveal: "Show selected region",
  },
};

// Original local vector artwork. Coordinates describe the viewer's left half;
// mirroring is geometric only. Semantic laterality is assigned in regionShape.
const MIRROR = "translate(320 0) scale(-1 1)";
const HEAD = "M160 7 C142 7 133 24 135 44 L138 61 Q141 73 150 78 L170 78 Q179 73 182 61 L185 44 C187 24 178 7 160 7Z";
const HALF_BODY = "M160 77 L149 76 Q151 91 143 98 L119 104 Q104 104 97 114 Q90 128 88 149 L78 181 Q74 193 76 206 Q65 226 64 244 L59 271 L53 284 L46 294 Q43 301 48 301 L55 295 L49 316 Q48 322 52 321 L59 305 L55 325 Q55 330 59 327 L65 308 L62 326 Q62 331 66 327 L71 309 L70 322 Q72 326 75 319 L82 294 Q84 284 77 274 L84 250 Q96 229 95 210 Q105 191 108 172 L114 151 Q118 177 119 194 Q116 215 114 231 Q108 244 111 272 Q108 300 114 329 L120 371 Q119 387 122 401 Q119 421 124 444 Q125 466 133 488 L136 526 L133 545 Q128 558 129 570 Q136 576 151 573 Q159 571 155 565 L151 547 L151 528 L155 483 Q160 454 152 431 L150 403 Q152 386 153 369 L157 322 Q161 305 160 294Z";

const SHAPES = {
  head: HEAD,
  neck: "M149 78 L171 78 Q169 92 179 99 L171 111 L149 111 L141 99 Q151 92 149 78Z",
  chest: "M119 108 Q133 100 146 107 L160 113 L174 107 Q187 100 201 108 L207 145 Q205 164 195 177 Q178 183 160 176 Q142 183 125 177 Q115 164 113 145Z",
  upper_back: "M142 98 Q160 107 178 98 L200 108 L207 141 L198 186 Q180 207 160 217 Q140 207 122 186 L113 141 L120 108Z",
  lower_back: "M122 185 Q141 205 160 213 Q179 205 198 185 L207 234 Q185 249 160 260 Q135 249 113 234Z",
  abdomen: "M124 178 Q143 184 160 178 Q177 184 196 178 L207 233 L188 248 L160 273 L132 248 L113 233Z",
  shoulder: "M118 104 Q103 101 96 118 Q88 131 88 150 Q97 150 112 138 L123 115Z",
  upper_arm: "M88 149 Q101 147 113 136 L109 168 Q105 187 96 196 L76 193 Q78 174 88 149Z",
  elbow: "M76 194 Q85 188 97 197 L96 211 Q86 218 72 207Z",
  forearm: "M72 218 Q85 224 95 219 Q96 232 84 252 L79 270 L61 268 L65 241Z",
  hand: "M61 270 L79 273 Q84 285 81 296 L74 324 Q70 329 68 324 Q63 334 60 328 Q54 333 53 326 Q48 327 47 320 L53 299 Q43 308 42 299 L50 286Z",
  hip: "M114 234 Q123 243 134 248 L130 283 Q119 289 110 279 Q106 251 114 234Z",
  hip_back: "M114 235 Q137 239 158 259 L158 285 Q145 307 115 290 Q107 269 114 235Z",
  groin: "M136 251 L159 275 L159 301 Q151 294 133 283Z",
  thigh_front: "M112 285 Q134 292 151 291 L159 308 L153 366 L149 387 Q133 392 121 384 L117 355 Q109 311 112 285Z",
  thigh_back: "M114 292 Q134 308 156 291 L159 312 L153 366 L149 387 Q135 393 121 384 L117 355Z",
  knee: "M122 390 Q135 395 149 390 L151 413 Q138 423 121 413Z",
  shin: "M122 418 Q137 425 151 419 Q160 448 154 478 L150 520 L135 520 L132 486 Q121 453 122 418Z",
  calf: "M122 418 Q137 425 151 419 Q160 448 154 478 L150 520 L135 520 L132 486 Q121 453 122 418Z",
  ankle: "M135 524 L151 524 L153 546 Q144 553 132 545Z",
  foot: "M132 549 Q143 555 153 549 L157 568 Q158 576 140 577 Q127 575 127 568Z",
};

// Joint targets extend into the empty space beside the body, avoiding a wide
// stroke that would steal hits from the adjacent limb or opposite ankle.
const JOINT_HITS = {
  neck: "M142 77 H178 V107 H142Z",
  elbow: "M64 190 H99 V218 H64Z",
  knee: "M113 387 H153 V418 H113Z",
  ankle: "M123 520 H155 V548 H123Z",
};

function regionShape(region, surface, view) {
  const shapes = view === "muscle" ? MUSCLE_SHAPES : SHAPES;
  const hits = view === "muscle" ? MUSCLE_JOINT_HITS : JOINT_HITS;
  if (!region.side) return { d: shapes[region.code], hit: hits[region.code] };
  const part = region.code.replace(/^(left|right)_/, "");
  const shapeKey = surface === "back" && shapes[`${part}_back`] ? `${part}_back` : part;
  const viewerLeft = region.side === (surface === "front" ? "right" : "left");
  return {
    d: shapes[shapeKey],
    hit: hits[shapeKey] || hits[part],
    transform: viewerLeft ? undefined : MIRROR,
  };
}

function Paired({ children }) {
  return <>{children}<g transform={MIRROR}>{children}</g></>;
}

function Paths({ paths, className }) {
  return paths.map((d, i) => <path className={className} d={d} key={i} />);
}


function SkeletonAnatomy({ back }) {
  return (
    <g className="pc-body-map__skeleton" aria-hidden="true" pointerEvents="none">
      <path className="pc-body-map__bone" d="M160 11 C141 11 136 29 139 48 L144 59 L146 70 Q160 82 174 70 L176 59 L181 48 C184 29 179 11 160 11Z" />
      {back ? <Paths className="pc-body-map__bone-detail" paths={[
        "M160 14 L158 24 L162 29 L158 35 L161 42 L160 50 M142 51 L149 49 L154 53 L160 50 L166 53 L171 49 L178 51",
        "M148 64 Q160 57 172 64",
      ]} /> : <>
        <path className="pc-body-map__bone-cavity" d="M144 44 Q150 40 156 44 L154 53 Q147 57 144 50Z M164 44 Q170 40 176 44 L176 50 Q173 57 166 53Z M160 51 L156 60 Q160 63 164 60Z" />
        <Paths className="pc-body-map__bone-detail" paths={[
          "M148 65 Q160 71 172 65 M150 69 Q160 75 170 69 M153 66 V71 M157 68 V72 M163 68 V72 M167 66 V71",
        ]} />
      </>}
      <g className="pc-body-map__vertebrae">
        {Array.from({ length: 7 }, (_, i) => <path className="pc-body-map__bone" key={`c${i}`} d={`M155 ${79 + i * 4} q5 -2 10 0 l1 3 q-6 3 -12 0Z`} />)}
        {Array.from({ length: 17 }, (_, i) => <path className="pc-body-map__bone" key={`v${i}`} d={`M${154 - Math.max(0, i - 10) * 0.35} ${110 + i * 8} q6 -3 ${12 + Math.max(0, i - 10) * 0.7} 0 l2 5 q-8 4 -16 0Z`} />)}
      </g>
      <Paired>
        {/* Curved paired ribs remain individually separated, even at small size. */}
        {Array.from({ length: 7 }, (_, i) => {
          const y = 119 + i * 9;
          const x = 126 - Math.sin((i / 7) * Math.PI) * 12;
          const d = `M154 ${y} C${x + 9} ${y - 12} ${x - 5} ${y - 5} ${x} ${y + 8} Q${x + 8} ${y + 21} 156 ${y + 13}`;
          return <g key={i}><path className="pc-body-map__bone-rim" d={d} /><path className="pc-body-map__rib" d={d} /></g>;
        })}
        {back && <>
          <path className="pc-body-map__bone pc-body-map__scapula" d="M119 118 Q132 115 144 124 Q140 143 129 163 Q120 156 118 138Z" />
          <path className="pc-body-map__bone-detail" d="M119 126 L140 131 M123 130 Q124 145 129 153" />
        </>}
        <Paths className="pc-body-map__bone" paths={[
          // Clavicle and humeral shaft; the joint surfaces are drawn below.
          "M156 109 Q144 103 133 109 L113 113 Q108 115 111 120 Q114 121 118 118 L135 115 Q145 110 155 115Z",
          "M98 137 L105 139 L93 184 L96 192 L81 192 L86 183Z",
          // Radius and ulna: two independent contours, not a single limb line.
          "M77 211 L83 213 L71 263 Q74 270 69 271 Q63 270 66 263Z",
          "M88 212 L94 212 L79 264 Q82 270 77 271 Q71 270 74 263Z",
          // Iliac wing, acetabulum and pubic/ischial arch form a hollow pelvis.
          "M151 246 Q136 236 121 239 Q111 244 115 261 L123 274 Q118 284 127 292 Q140 301 154 280 L159 267 L151 265 Q146 278 136 287 Q126 289 128 281 L140 269 L148 263Z",
          // Femur, tibia and fibula meet the separate, readable joint contours.
          "M139 279 Q146 273 150 280 Q154 287 145 290 L132 298 L138 382 L130 383 L124 301 Q115 301 117 292 Q119 285 125 290 L134 285Q133 281 139 279Z",
          "M128 416 L141 416 L138 430 L146 523 L139 524 L130 431Z",
          "M150 417 Q155 416 154 424 L149 480 L151 525 L146 529 L143 481 L149 428Z",
        ]} />
        {/* Visual anatomy only: the stable region/highlight/hit paths are separate. */}
        <g className="pc-body-map__joint" data-anatomy-joint="shoulder">
          {/* Acromion and glenoid surround, but do not merge with, the humeral head. */}
          <path className="pc-body-map__bone" d="M120 111 Q112 108 103 113 L98 118 L101 121 Q111 116 119 118Z" />
          <path className="pc-body-map__bone" d="M117 121 Q113 127 113 135 L118 140 L121 136 Q117 131 122 124Z" />
          <path className="pc-body-map__bone" d="M105 122 C94 119 91 131 96 138 Q101 144 108 139 Q113 135 111 128 Q110 124 105 122Z" />
          <path className="pc-body-map__bone-detail" d="M97 128 Q100 123 105 125 M98 138 Q104 140 108 135" />
        </g>
        <g className="pc-body-map__joint" data-anatomy-joint="elbow">
          {/* Distal humeral condyles sit above the radial head and ulnar notch. */}
          <path className="pc-body-map__bone" d="M86 184 L92 184 L96 190 Q100 195 94 199 Q90 201 87 197 Q81 202 77 197 Q74 191 81 189Z" />
          <path className="pc-body-map__bone" d="M77 202 Q81 200 85 203 L84 208 Q80 211 76 207Z" />
          <path className="pc-body-map__bone" d="M90 201 Q94 202 96 198 L97 205 L94 214 L88 214 L87 207Z" />
          <path className="pc-body-map__bone-detail" d={back ? "M87 188 Q93 189 91 195 L88 196 M90 206 L93 205" : "M85 190 Q83 193 85 196 M90 191 L93 195 M78 205 L82 206"} />
        </g>
        <g className="pc-body-map__joint" data-anatomy-joint="knee">
          <path className="pc-body-map__bone" d="M130 380 L138 380 Q140 385 146 388 Q150 395 144 401 Q139 404 135 399 Q130 404 124 400 Q119 395 124 388Z" />
          <path className="pc-body-map__bone" d="M124 406 Q130 404 135 407 Q141 404 147 406 L146 412 Q140 417 139 422 L131 422 Q128 415 124 412Z" />
          {back ? <path className="pc-body-map__bone-detail" d="M135 386 L135 397 M126 391 Q122 396 128 399 M143 391 Q147 396 141 399 M126 410 Q135 414 144 410" /> : <>
            <path className="pc-body-map__bone pc-body-map__patella" d="M128 394 Q135 390 142 395 Q145 400 139 407 L135 410 L130 406 Q125 400 128 394Z" />
            <path className="pc-body-map__bone-detail" d="M131 396 Q135 394 139 397 M129 414 L141 414" />
          </>}
        </g>
        <g className="pc-body-map__joint" data-anatomy-joint="ankle">
          {/* Malleoli frame the talar dome; a clear gap separates it from the foot. */}
          <path className="pc-body-map__bone" d="M138 522 L146 521 L149 527 L148 532 L143 530 L138 532 L138 537 Q133 539 133 534 L135 528Z" />
          <path className="pc-body-map__bone" d="M148 525 L152 524 L154 534 Q155 539 151 541 L149 538Z" />
          <path className="pc-body-map__bone" d="M139 534 Q143 531 147 534 L148 540 Q143 545 138 540Z" />
          <path className="pc-body-map__bone-detail" d="M140 537 Q143 535 146 537" />
        </g>
        <g className="pc-body-map__foot-bones">
          <path className="pc-body-map__bone" d={back ? "M136 549 Q142 546 148 549 L150 556 Q144 562 135 557Z" : "M136 549 L144 547 L149 550 L147 555 L140 557 L133 554Z"} />
          <path className="pc-body-map__bone-detail" d={back ? "M139 550 Q137 554 141 556" : "M142 549 L142 554 M136 552 L139 554 M146 551 L145 553"} />
        </g>
        <Paths className="pc-body-map__bone-detail" paths={[
          "M122 247 Q132 244 142 251 M127 256 L134 268 M121 262 L127 268 M130 283 L139 275",
        ]} />
        {/* Carpals and separated metacarpal/phalangeal chains. */}
        {[0, 1, 2].map((i) => <ellipse className="pc-body-map__bone" key={i} cx={67 + i * 4} cy={275 + (i % 2) * 2} rx="2.5" ry="3" />)}
        <Paths className="pc-body-map__small-bone" paths={[
          "M64 280 L58 289 M57 293 L50 299",
          "M66 281 L62 296 M61 300 L57 309 M56 312 L53 319",
          "M70 282 L68 300 M67 304 L64 313 M63 317 L61 325",
          "M74 282 L73 299 M72 303 L70 313 M69 317 L68 323",
          "M78 284 L78 296 M77 300 L75 308 M74 312 L73 317",
          "M134 559 L132 566 M132 569 L133 571 M139 561 L138 568 M138 571 L140 572",
          "M144 560 L144 568 M144 571 L146 572 M148 559 L150 566 M150 569 L152 570",
        ]} />
      </Paired>
      <path className="pc-body-map__bone" d="M151 245 Q160 249 169 245 L167 265 L160 279 L153 265Z" />
      <Paths className="pc-body-map__bone-detail" paths={[
        "M155 252 H165 M155 257 H165 M156 263 H164 M157 268 H163",
      ]} />
      {!back && <path className="pc-body-map__bone" d="M157 116 Q160 113 163 116 L165 128 L162 173 L160 181 L158 173 L155 128Z" />}
    </g>
  );
}

function ViewToggle({ label, options, value, onChange }) {
  return (
    <div className="pc-body-map__toggle" role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button key={key} type="button" aria-pressed={value === key} onClick={() => onChange(key)}>{text}</button>
      ))}
    </div>
  );
}

export default function BodyRegionSelector({ value, onChange, lang = "de", readOnly = false, compact = false, renderSelection }) {
  const copy = COPY[lang === "en" ? "en" : "de"];
  const uid = `body-map-${useId().replace(/:/g, "")}`;
  const selectedSurface = bodyRegionSurface(value);
  const [chosenSurface, setSurface] = useState(() => selectedSurface === "back" ? "back" : "front");
  const [view, setView] = useState("muscle");
  // Read-only previews always follow their record, including prop updates.
  const surface = readOnly ? (selectedSurface === "back" ? "back" : "front") : chosenSurface;
  const hiddenSelection = selectedSurface && selectedSurface !== "both" && selectedSurface !== surface;
  const regions = BODY_REGIONS.filter((region) => region.code !== "other" && (region.surface === "both" || region.surface === surface));
  const selectedLabel = bodyRegionLabel(value, lang, surface);
  const select = (code) => { if (!readOnly) onChange?.(code); };

  return (
    <div className={`pc-body-map${compact ? " pc-body-map--compact" : ""}${readOnly ? " pc-body-map--readonly" : ""}`} data-surface={surface} data-view={view}>
      {!readOnly && <div className="pc-body-map__controls">
        <ViewToggle label={copy.surface} options={[["front", copy.front], ["back", copy.back]]} value={surface} onChange={setSurface} />
        <ViewToggle label={copy.anatomy} options={[["muscle", copy.muscle], ["skeleton", copy.skeleton]]} value={view} onChange={setView} />
      </div>}
      <div className="pc-body-map__canvas">
        <svg
          className="pc-body-map__svg" viewBox={view === "muscle" ? "40 0 240 480" : "0 0 320 596"} xmlns="http://www.w3.org/2000/svg"
          role={readOnly ? "img" : "group"} aria-labelledby={`${uid}-title`} aria-describedby={readOnly ? undefined : `${uid}-help`}
        >
          <title id={`${uid}-title`}>{`${copy.map} · ${copy[surface]} · ${copy[view]}${readOnly && selectedLabel ? ` · ${selectedLabel}` : ""}`}</title>
          {!readOnly && <desc id={`${uid}-help`}>{copy.instruction}</desc>}
          <defs>
            <linearGradient id={`${uid}-bone`} x1="0" x2="1">
              <stop offset="0" className="pc-body-map__bone-shadow" /><stop offset=".4" className="pc-body-map__bone-light" /><stop offset="1" className="pc-body-map__bone-shadow" />
            </linearGradient>
            <radialGradient id={`${uid}-halo`}><stop stopColor="currentColor" stopOpacity=".065" /><stop offset="1" stopColor="currentColor" stopOpacity="0" /></radialGradient>
          </defs>
          <g aria-hidden="true" pointerEvents="none">
            <ellipse cx="160" cy={view === "muscle" ? 240 : 290} rx={view === "muscle" ? 118 : 152} ry={view === "muscle" ? 230 : 290} fill={`url(#${uid}-halo)`} className="pc-body-map__halo" />
            {view === "skeleton" && <path className="pc-body-map__guide" d="M160 10 V582" />}
            <text x="46" y={view === "muscle" ? 100 : 119} className="pc-body-map__side">{surface === "front" ? copy.right : copy.left}</text>
            <text x="274" y={view === "muscle" ? 100 : 119} className="pc-body-map__side">{surface === "front" ? copy.left : copy.right}</text>
          </g>
          <g style={{ "--body-bone-fill": `url(#${uid}-bone)` }}>
            {view === "skeleton" && <g className="pc-body-map__silhouette" aria-hidden="true" pointerEvents="none">
              <Paired><path d={HALF_BODY} /></Paired><path d={HEAD} />
            </g>}
            {view === "muscle" ? <BodyMuscleAnatomy back={surface === "back"} /> : <SkeletonAnatomy back={surface === "back"} />}
            <g className="pc-body-map__regions" aria-hidden={readOnly ? true : undefined}>
              {regions.map((region) => {
                const shape = regionShape(region, surface, view);
                const selected = value === region.code;
                return (
                  <g
                    key={region.code} transform={shape.transform} className="pc-body-map__region"
                    data-region={region.code} data-selected={selected ? "true" : "false"}
                    role={readOnly ? undefined : "button"} tabIndex={readOnly ? undefined : 0}
                    aria-label={readOnly ? undefined : copy.select(bodyRegionLabel(region.code, lang, surface))}
                    aria-pressed={readOnly ? undefined : selected}
                    onClick={readOnly ? undefined : () => select(region.code)}
                    onKeyDown={readOnly ? undefined : (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (!event.repeat) select(region.code);
                      }
                    }}
                  >
                    {shape.hit && <path className="pc-body-map__hit" d={shape.hit} />}
                    <path className="pc-body-map__region-shape" d={shape.d} />
                    {!readOnly && <title>{bodyRegionLabel(region.code, lang, surface)}</title>}
                  </g>
                );
              })}
            </g>
          </g>
        </svg>
      </div>
      {!readOnly && <>
        <div className="pc-body-map__notice" role="status">
          {hiddenSelection && <><span>{copy.hidden}</span><button type="button" onClick={() => setSurface(selectedSurface)}>{copy.reveal}</button></>}
        </div>
        <button type="button" className="pc-body-map__other" aria-pressed={value === "other"} onClick={() => select("other")}>
          <span className="pc-body-map__other-mark" aria-hidden="true">{value === "other" ? "✓" : "+"}</span>
          {bodyRegionLabel("other", lang)}
        </button>
      </>}
      {renderSelection?.({ label: selectedLabel })}
    </div>
  );
}
